import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Mic, MicOff, Volume2, ArrowRight, Sparkles, Phone } from "lucide-react";
import { OWNER, PORTRAIT_URL, VOICE_GREETING } from "@/data/portfolio";
import { useReveal } from "@/hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Hero() {
  const { ref, visible } = useReveal(0.1);
  const audioRef = useRef(null);
  const [speaking, setSpeaking] = useState(false);
  const [muted, setMuted] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [greeted, setGreeted] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);

  const fetchTts = useCallback(async () => {
    try {
      setLoadingAudio(true);
      const { data } = await axios.post(`${API}/tts`, {
        text: VOICE_GREETING,
        voice: "onyx",
      });
      if (data && data.audio_base64) {
        setAudioSrc(`data:${data.mime || "audio/mp3"};base64,${data.audio_base64}`);
      }
    } catch (e) {
      // TTS failure should not break the site
      console.warn("TTS unavailable, falling back to browser speech");
    } finally {
      setLoadingAudio(false);
    }
  }, []);

  useEffect(() => {
    fetchTts();
  }, [fetchTts]);

  // Try autoplay once audio source is ready (may be blocked; user can click Play)
  useEffect(() => {
    if (!audioSrc || greeted) return;
    const el = audioRef.current;
    if (!el) return;
    el.src = audioSrc;
    el.muted = muted;
    const tryPlay = async () => {
      try {
        await el.play();
        setSpeaking(true);
        setGreeted(true);
      } catch (_) {
        // Autoplay blocked; user must interact
      }
    };
    tryPlay();
    // eslint-disable-next-line
  }, [audioSrc]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const onEnded = () => setSpeaking(false);

  // Pick the best available Indian male voice from the browser (fallback path)
  const pickBrowserVoice = () => {
    const voices = window.speechSynthesis?.getVoices() || [];
    if (voices.length === 0) return null;
    // Preference order: Indian English male > any English male > any male > first Indian > first
    const candidates = [
      (v) => /en[-_]IN/i.test(v.lang) && /male/i.test(v.name) && !/female/i.test(v.name),
      (v) => /ravi|prabhat|hemant|rishi/i.test(v.name),
      (v) => /en[-_]IN/i.test(v.lang),
      (v) => /male/i.test(v.name) && !/female/i.test(v.name),
      (v) => /google.*(uk|us|india).*male/i.test(v.name),
      (v) => /alex|daniel|fred|david/i.test(v.name),
    ];
    for (const test of candidates) {
      const found = voices.find(test);
      if (found) return found;
    }
    return voices[0];
  };

  const playGreeting = async () => {
    const el = audioRef.current;
    if (!el || !audioSrc) {
      // fallback to browser speech synth
      const speak = () => {
        const utter = new window.SpeechSynthesisUtterance(VOICE_GREETING);
        utter.rate = 0.95;
        utter.pitch = 0.9;
        utter.lang = "en-IN";
        const v = pickBrowserVoice();
        if (v) utter.voice = v;
        utter.onstart = () => setSpeaking(true);
        utter.onend = () => setSpeaking(false);
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utter);
      };
      // voices may load async; wait once if needed
      if (window.speechSynthesis && window.speechSynthesis.getVoices().length === 0) {
        window.speechSynthesis.onvoiceschanged = () => { speak(); window.speechSynthesis.onvoiceschanged = null; };
      } else {
        speak();
      }
      return;
    }
    try {
      el.currentTime = 0;
      el.muted = muted;
      await el.play();
      setSpeaking(true);
      setGreeted(true);
    } catch (e) {
      // ignored
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
      {/* background elements */}
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#FF3B30]/10 blur-[120px] pointer-events-none" />
      <div className="absolute top-1/3 -right-32 w-[420px] h-[420px] rounded-full bg-[#FF3B30]/8 blur-[120px] pointer-events-none" />

      <div
        ref={ref}
        className={`relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center fade-in-up ${visible ? "in" : ""}`}
      >
        {/* LEFT */}
        <div className="lg:col-span-7 relative z-10">
          <div className="section-label mb-6" data-testid="hero-eyebrow">
            <Sparkles size={14} /> Premium Web Studio
          </div>

          <h1 className="heading-hero text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mb-6">
            Hi, I'm
            <span className="block">
              <span className="text-[#FF3B30]">{OWNER.firstName}</span>.
            </span>
            <span className="block text-white/80 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mt-3 font-display font-semibold tracking-tight">
              {OWNER.title}
            </span>
          </h1>

          <p className="text-base md:text-lg text-white/70 max-w-xl leading-relaxed mb-10" data-testid="hero-tagline">
            {OWNER.tagline}
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-8">
            <a href="#portfolio" onClick={scrollTo("portfolio")} className="btn-primary" data-testid="hero-view-work-btn">
              View My Work <ArrowRight size={18} />
            </a>
            <a href="#contact" onClick={scrollTo("contact")} className="btn-ghost" data-testid="hero-hire-me-btn">
              Hire Me
            </a>
            <a href={`tel:${OWNER.phone}`} className="btn-ghost" data-testid="hero-call-btn">
              <Phone size={16} /> Dial Enquiry
            </a>
          </div>

          {/* Voice greeting widget */}
          <div
            className="inline-flex items-center gap-3 glass-card !border-white/10 px-4 py-3 rounded-full"
            data-testid="voice-greeting-widget"
          >
            <button
              onClick={speaking ? stopSpeaking : playGreeting}
              className="relative w-11 h-11 rounded-full grid place-items-center bg-[#FF3B30] text-white shadow-lg shadow-red-500/30"
              aria-label={speaking ? "Stop greeting" : "Play greeting"}
              data-testid="voice-play-btn"
              disabled={loadingAudio}
            >
              <Volume2 size={18} />
              {speaking && (
                <>
                  <span className="absolute inset-0 rounded-full border-2 border-[#FF3B30] animate-pulse-ring" />
                  <span className="absolute inset-0 rounded-full border-2 border-[#FF3B30]/60 animate-pulse-ring" style={{ animationDelay: '0.8s' }} />
                </>
              )}
            </button>
            <div className="pr-2">
              <p className="text-xs uppercase tracking-widest text-white/50">Voice greeting</p>
              <p className="text-sm font-medium text-white/90">
                {speaking ? "Speaking..." : loadingAudio ? "Loading..." : "Tap to listen"}
              </p>
            </div>
            <button
              onClick={() => setMuted((m) => !m)}
              className="w-10 h-10 rounded-full grid place-items-center border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
              aria-label="Toggle mute"
              data-testid="voice-mute-btn"
            >
              {muted ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
          </div>
          <audio ref={audioRef} onEnded={onEnded} onPause={() => setSpeaking(false)} className="hidden" data-testid="hero-audio-el" />
        </div>

        {/* RIGHT - Portrait */}
        <div className="lg:col-span-5 relative">
          <div className="relative mx-auto max-w-md lg:max-w-none group">
            {/* soft red aura */}
            <div className="portrait-aura" />
            {/* subtle rotating gradient border */}
            <div className="absolute -inset-[2px] rounded-[28px] bg-gradient-to-br from-[#FF3B30]/60 via-white/10 to-[#FF3B30]/40 opacity-70 blur-[1px]" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#0b0b0d] red-glow">
              <img
                src={PORTRAIT_URL}
                alt="Sunil KP - Website Designer & Frontend Developer"
                className="w-full h-auto object-cover animate-float-slow transition-transform duration-700 group-hover:scale-[1.02]"
                loading="eager"
                data-testid="hero-portrait"
              />
              {/* Corner accent lines */}
              <span className="pointer-events-none absolute top-3 left-3 w-8 h-8 border-t-2 border-l-2 border-[#FF3B30] rounded-tl-2xl" />
              <span className="pointer-events-none absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-[#FF3B30] rounded-tr-2xl" />
              <span className="pointer-events-none absolute bottom-3 left-3 w-8 h-8 border-b-2 border-l-2 border-[#FF3B30] rounded-bl-2xl" />
              <span className="pointer-events-none absolute bottom-3 right-3 w-8 h-8 border-b-2 border-r-2 border-[#FF3B30] rounded-br-2xl" />
            </div>

            {/* floating stats */}
            <div className="hidden md:flex absolute -left-8 top-24 glass-card !bg-black/70 rounded-2xl p-4 min-w-[150px]">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-white/50">Projects</p>
                <p className="text-2xl font-display font-bold text-white">250<span className="text-[#FF3B30]">+</span></p>
              </div>
            </div>
            <div className="hidden md:flex absolute -right-6 bottom-28 glass-card !bg-black/70 rounded-2xl p-4 min-w-[150px]">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-white/50">Clients</p>
                <p className="text-2xl font-display font-bold text-white">180<span className="text-[#FF3B30]">+</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
